import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FileSubresourcePath, Ref, SimplePagination, Ticket, TicketFile} from '@lifeislife/lifeislife-domain';
import {Observable, Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {FileDownloadComponent, RefWithFileRef, TicketFilesHelperService, TicketFileWithFile} from '@lifeislife/lifeislife-components';
import {ShellMessageService} from '../../service/shell-message-service';
import {CustomerInsuranceInformationRequestHelperService} from '../../service/customer-insurance-information-request-helper.service';
import {isSameRef} from '../../service/utils';
import {myThrottleTime} from '../../../../../lifeislife-front/src/app/commons/util/operators/throttle-time';
import {ShellBannerService} from '../../service/shell-banner-service';

@Component({
  selector: 'cf-insurance-request-draft-dialog',
  templateUrl: './insurance-request-draft-dialog.component.html',
  styleUrls: ['./insurance-request-draft-dialog.component.scss'],
  providers: [TicketFilesHelperService],
})
export class InsuranceRequestDraftDialogComponent implements OnInit, OnDestroy {

  @Input()
  set ticket(value: Ticket) {
    this.loadDraft(value);
  }

  draft: Ticket | null;

  draftFiles$: Observable<TicketFileWithFile[]>;
  draftFileCount$: Observable<number>;
  draftFileLoading$: Observable<boolean>;
  draftFilePaginaition$: Observable<SimplePagination>;
  draftFileUploadPath$: Observable<FileSubresourcePath<Ticket>>;

  draftUpdating$: Observable<boolean>;
  draftCommitting$: Observable<boolean>;

  private ticketSubscription: Subscription;
  private syncChangesSource$ = new Subject<boolean>();

  @ViewChild(FileDownloadComponent, {static: true})
  private fileDownloadComponent: FileDownloadComponent;

  constructor(
    private customerInsuranceInformationRequestHelperService: CustomerInsuranceInformationRequestHelperService,
    private ticketFileHelper: TicketFilesHelperService,
    private messageService: ShellMessageService,
    private bannerService: ShellBannerService,
  ) {
  }

  ngOnInit(): void {
    this.ticketSubscription = new Subscription();

    this.draftFiles$ = this.ticketFileHelper.ticketFilesWithFiles$;
    this.draftFilePaginaition$ = this.ticketFileHelper.pagination$;
    this.draftFileLoading$ = this.ticketFileHelper.loading$;
    this.draftFileCount$ = this.ticketFileHelper.totalCount$;
    this.draftFileUploadPath$ = this.ticketFileHelper.newFileUploadPath$;

  }

  ngOnDestroy() {
    if (this.ticketSubscription) {
      this.ticketSubscription.unsubscribe();
    }
  }

  onEditingDraftDialogVisibleChange(visible: boolean) {
    if (!visible) {
      this.customerInsuranceInformationRequestHelperService.cancelDraft(this.draft, false);
    }
  }

  syncChanges() {
    this.syncChangesSource$.next(true);
  }

  onAttachmentPaginationChange(pagination: SimplePagination<any, any, any>) {
    this.ticketFileHelper.pagination$.next(pagination);
  }

  onAttachmentRemoveClick(refWithFileRef: RefWithFileRef) {
    this.ticketFileHelper.delete$(refWithFileRef).subscribe(() => {
      },
      error => this.messageService.addError(`Impossible de supprimer le ficher`, error));
  }

  onAttachmentsUploaded(event: Ref<TicketFile>[]) {
    if (event == null || event.length === 0) {
      return;
    }
    this.ticketFileHelper.reload();
  }

  onAttachmentUploadAborted(event: RefWithFileRef) {
  }

  onAttachmentUploadErrored(error: any) {
    this.messageService.addError(`Impossible d'envoyer le fichier`, error);
  }

  onFileClicked(withFileRef: RefWithFileRef) {
    this.ticketFileHelper.getDownloadUrl$(withFileRef)
      .subscribe(url => this.fileDownloadComponent.downloadFile(url));
  }

  onCommitDraftClick() {
    if (this.ticketSubscription) {
      this.ticketSubscription.unsubscribe();
      this.ticketSubscription = null;
    }
    this.customerInsuranceInformationRequestHelperService.commitDraft(this.draft);
  }

  onDiscardDraftClick() {
    if (this.ticketSubscription) {
      this.ticketSubscription.unsubscribe();
      this.ticketSubscription = null;
    }
    this.customerInsuranceInformationRequestHelperService.cancelDraft(this.draft, true);
  }


  private loadDraft(ticket: Ticket) {
    const prevDraft = this.draft;
    if (this.ticketSubscription) {
      this.ticketSubscription.unsubscribe();
      this.ticketSubscription = null;
    }
    this.ticketSubscription = new Subscription();
    if (ticket != null && prevDraft != null && isSameRef(ticket, prevDraft)) {
      // Apply cur change over server version
      this.draft = Object.assign(ticket, prevDraft);
    } else {
      this.draft = ticket;
      this.ticketFileHelper.initForTicketRef(ticket);
      this.draftUpdating$ = this.customerInsuranceInformationRequestHelperService.isUpdating$(ticket);
      this.draftCommitting$ = this.customerInsuranceInformationRequestHelperService.isCommitting$(ticket);


      const changesSubscription = this.syncChangesSource$.pipe(
        debounceTime(1000),
      ).subscribe(() => this.customerInsuranceInformationRequestHelperService.updateDraft(this.draft));
      this.ticketSubscription.add(changesSubscription);
    }

    if (this.draft) {
      this.bannerService.clearBanner(ShellBannerService.BANNER_ID_INSURANCE_INFO_REQUEST);
    }
  }

}
